/**
 * Audit Events Table Component
 *
 * Reusable component for displaying audit events in a grouped table with an
 * optional details modal.
 *
 * Supports two display modes controlled by the presence of pagination props:
 *   - **Pagination mode** (AdminDashboardPage): pass `currentPage`, `onNextPage`,
 *     and `onPrevPage` to render Previous/Next buttons.
 *   - **Infinite scroll mode** (AdminAuditEventsPage): omit pagination props;
 *     the parent handles the sentinel element and "load more" indicator.
 *
 * Optional `stickyColumnHeaderTopPx` (AdminAuditEventsPage): viewport sticky column
 * headers under the fixed admin shell — omit when embedded (dashboard strip, modal).
 *
 * Resizable column widths use {@link useResizableColumnWidths} with the same
 * boundary-drag model as Ops / Scheduled Items; preferences persist under
 * `admin-audit-events-column-widths-v1`.
 *
 * The column header must **not** sit inside Mantine `Card`/`Paper`: their root
 * often applies clipping that prevents `position: sticky` from sticking to the
 * viewport (same class of bug as `Table.ScrollContainer` + sticky).
 *
 * Layout contract (same as AdminOperationsPage): sticky {@link Table} thead +
 * real `Table.Tr` / `Table.Td` body cells under one `colgroup`. Do **not** put
 * flex-width "fake columns" inside a single `colSpan` cell — when the table is
 * `width: 100%` the header columns stretch and fixed-px flex children drift
 * one column left of their headers.
 */

import { useCallback, useState } from 'react';
import {
  Text,
  Card,
  Group,
  Badge,
  ActionIcon,
  Tooltip,
  Code,
  Anchor,
  Divider,
  Box,
  Stack,
  Button,
  Loader,
  Center,
  Alert,
  Table,
  UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconEye,
  IconExternalLink,
  IconChevronRight,
  IconAlertCircle,
} from '@tabler/icons-react';
import { actionColors, brandColors, semanticColors } from '../../config/theme';
import { formatInstantLocal, compareApiUtcInstants } from '../../utils/formatDateTime';
import { useModalZIndex } from '../../hooks/useModalZIndex';
import { AppModal } from '../common/AppModal';
import { ColumnResizeGrip } from '../common/ColumnResizeGrip';
import { useResizableColumnWidths } from '../../hooks/useResizableColumnWidths';
import { ADMIN_STICKY_TABLE_HEADER_TH_STYLE } from './AdminStickyListLayout';
import { getAdminTableProps } from './getAdminTableProps';

// ──────────────────────────────────────────────
// Resizable columns (shared colgroup for thead + tbody)
// ──────────────────────────────────────────────

const AUDIT_EVENTS_COLUMN_KEYS = [
  'leading',
  'time',
  'eventType',
  'status',
  'user',
  'summary',
  'actions',
] as const;

type AuditEventsColumnKey = (typeof AUDIT_EVENTS_COLUMN_KEYS)[number];

const AUDIT_DEFAULT_WIDTHS: Record<AuditEventsColumnKey, number> = {
  leading: 40,
  time: 190,
  eventType: 200,
  status: 80,
  user: 120,
  summary: 240,
  actions: 60,
};

const AUDIT_MIN_WIDTHS: Record<AuditEventsColumnKey, number> = {
  leading: 28,
  time: 100,
  eventType: 100,
  status: 64,
  user: 72,
  summary: 100,
  actions: 44,
};

const AUDIT_MAX_WIDTHS: Record<AuditEventsColumnKey, number> = {
  leading: 64,
  time: 400,
  eventType: 560,
  status: 200,
  user: 400,
  summary: 800,
  actions: 100,
};

/** `localStorage` key for {@link AUDIT_DEFAULT_WIDTHS} persistence. */
const AUDIT_COLUMN_WIDTHS_STORAGE_KEY = 'admin-audit-events-column-widths-v1';

/**
 * Accessibility strings for each vertical boundary (between column `i` and
 * `i + 1`).
 */
const AUDIT_COLUMN_BOUNDARY_LABELS = [
  'chevron and Time',
  'Time and Event Type',
  'Event Type and Status',
  'Status and User',
  'User and Summary',
  'Summary and Actions',
] as const;

// ──────────────────────────────────────────────
// Shared types
// ──────────────────────────────────────────────

/** A single audit event returned from the API. */
export interface AuditEvent {
  id: string;
  occurredAtUtc: string;
  userId: string | null;
  userName: string | null;
  actorUserId: string | null;
  actorUserName: string | null;
  eventTypeCode: string;
  entityType: string | null;
  entityId: number | null;
  statusCode: string;
  summary: string | null;
  metadata: Record<string, unknown> | null;
  correlationId: string | null;
  traceId: string | null;
  appInsightsTraceUrl: string | null;
  isUserVisible: boolean;
  visibilityScope: string;
}

/** Paged result returned from the audit-events API. */
export interface AuditEventQueryResult {
  items: AuditEvent[];
  totalCount: number;
  nextCursor: string | null;
  hasMore: boolean;
  isTruncated: boolean;
  pageSize: number;
}

/**
 * Grouped audit events by correlation ID.
 * Events without a correlation ID are treated as single-item groups.
 */
interface EventGroup {
  correlationId: string | null;
  primaryEvent: AuditEvent;
  events: AuditEvent[];
  hasMultiple: boolean;
}

// ──────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────

/**
 * Groups events by correlation ID.
 * Events without a correlation ID are kept as individual groups.
 * Within each group, events are ordered by time (most recent first).
 */
export const groupEventsByCorrelation = (events: AuditEvent[]): EventGroup[] => {
  const groupMap = new Map<string, AuditEvent[]>();
  const ungroupedEvents: AuditEvent[] = [];

  for (const event of events) {
    if (event.correlationId) {
      const existing = groupMap.get(event.correlationId) || [];
      existing.push(event);
      groupMap.set(event.correlationId, existing);
    } else {
      ungroupedEvents.push(event);
    }
  }

  const groups: EventGroup[] = [];

  for (const [correlationId, groupEvents] of groupMap) {
    groupEvents.sort(
      (a, b) =>
        compareApiUtcInstants(b.occurredAtUtc, a.occurredAtUtc)
    );
    groups.push({
      correlationId,
      primaryEvent: groupEvents[0],
      events: groupEvents,
      hasMultiple: groupEvents.length > 1,
    });
  }

  for (const event of ungroupedEvents) {
    groups.push({
      correlationId: null,
      primaryEvent: event,
      events: [event],
      hasMultiple: false,
    });
  }

  groups.sort(
    (a, b) =>
      compareApiUtcInstants(b.primaryEvent.occurredAtUtc, a.primaryEvent.occurredAtUtc)
  );

  return groups;
};

/** Returns a Mantine colour token for the given status code. */
export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'succeeded':
      return brandColors.success;
    case 'failed':
      return brandColors.danger;
    case 'info':
      return brandColors.info;
    case 'attempted':
      return brandColors.warmAccent;
    default:
      return brandColors.neutral;
  }
};

/** Returns a human-readable display name for the event's user. */
export const getUserDisplay = (event: AuditEvent): string => {
  if (event.userName) return event.userName;
  if (event.actorUserName) return event.actorUserName;
  if (event.userId) return event.userId.substring(0, 8) + '...';
  if (event.actorUserId) return event.actorUserId.substring(0, 8) + '...';
  return '-';
};

/**
 * Formats the loaded-row count label for admin audit lists (#1192).
 * When totalCount is 0 and not truncated, the API skipped COUNT (unfiltered or cursor page).
 */
export function formatAuditEventsCountLabel(
  loadedCount: number,
  totalCount: number,
  isTruncated: boolean
): string {
  if (totalCount <= 0 && !isTruncated) {
    return `Showing ${loadedCount.toLocaleString()} events`;
  }

  return `Showing ${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()} events${
    isTruncated ? ' (truncated)' : ''
  }`;
}

// ──────────────────────────────────────────────
// Component props
// ──────────────────────────────────────────────

export interface AuditEventsTableProps {
  /** The current page of audit events to display. */
  events: AuditEvent[];
  /** Whether data is currently being fetched. */
  loading: boolean;
  /** Error message to display, if any. */
  error: string | null;
  /** Total number of events matching the query (may be truncated). */
  totalCount: number;
  /** Whether more pages exist after the current page. */
  hasMore: boolean;
  /** Whether the total count has been truncated (> 10 000). */
  isTruncated: boolean;

  /**
   * Pagination-mode props (optional).
   * When all three are provided, Previous/Next buttons are rendered.
   * Omit these when the parent manages infinite scrolling.
   */
  currentPage?: number;
  /** Callback to navigate to the next page. */
  onNextPage?: () => void;
  /** Callback to navigate to the previous page. */
  onPrevPage?: () => void;

  /**
   * When the admin page uses viewport scroll, pass `APP_HEADER_HEIGHT` plus the
   * measured filter-band height so the column header row sticks under the shell
   * header (same idea as EventTypeTabsPanel). Omit on dashboard / modal embeds.
   */
  stickyColumnHeaderTopPx?: number;
}

// ──────────────────────────────────────────────
// AuditEventsTable
// ──────────────────────────────────────────────

/**
 * Renders a sticky-header table of audit events grouped by correlation ID,
 * with an optional details modal. Multi-event groups expand via a chevron
 * (real nested `Table.Tr` rows — not Accordion-in-colSpan). When pagination
 * props (`currentPage`, `onNextPage`, `onPrevPage`) are provided, Previous/Next
 * buttons are shown; otherwise the parent is expected to handle infinite scrolling.
 */
export const AuditEventsTable = ({
  events,
  loading,
  error,
  totalCount,
  hasMore,
  isTruncated,
  currentPage,
  onNextPage,
  onPrevPage,
  stickyColumnHeaderTopPx,
}: AuditEventsTableProps) => {
  // Details modal state
  const [detailsOpened, { open: openDetails, close: closeDetails }] =
    useDisclosure(false);
  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | null>(null);

  // Stack above parent modals (e.g. Ops / e-card detail embeds this table).
  const detailsZIndex = useModalZIndex(detailsOpened);

  /** Correlation / primary ids whose child events are visible. */
  const [expandedGroupKeys, setExpandedGroupKeys] = useState<Set<string>>(
    () => new Set(),
  );

  const { columnWidths, handleBoundaryPointerDown } =
    useResizableColumnWidths<AuditEventsColumnKey>({
      storageKey: AUDIT_COLUMN_WIDTHS_STORAGE_KEY,
      columnKeys: AUDIT_EVENTS_COLUMN_KEYS,
      defaultWidths: AUDIT_DEFAULT_WIDTHS,
      minWidths: AUDIT_MIN_WIDTHS,
      maxWidths: AUDIT_MAX_WIDTHS,
    });

  const headerThStyle = ADMIN_STICKY_TABLE_HEADER_TH_STYLE;

  /** Opens the details modal for an event. */
  const handleViewDetails = (event: AuditEvent) => {
    setSelectedEvent(event);
    openDetails();
  };

  const toggleGroupExpanded = useCallback((groupKey: string) => {
    setExpandedGroupKeys((prev) => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const groups = groupEventsByCorrelation(events);

  return (
    <>
      {/* Truncation warning */}
      {isTruncated && (
        <Alert color={brandColors.warning} title="Results Limited">
          Results are limited to 10,000 records. Refine your filters to see more
          specific results.
        </Alert>
      )}

      {/* Error alert */}
      {error && (
        <Alert color={brandColors.danger} icon={<IconAlertCircle size={16} />}>
          {error}
        </Alert>
      )}

      {/* Table body */}
      {loading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : events.length === 0 ? (
        <Card shadow="sm" p="lg" radius="md" withBorder>
          <Center py="lg">
            <Text c={semanticColors.text.secondary}>No audit events found.</Text>
          </Center>
        </Card>
      ) : (
        <Box style={{ width: '100%', maxWidth: '100%' }}>
          {/*
            Sticky thead + real tbody cells share one colgroup (Ops pattern).
            Nested correlation children render as additional Table.Tr rows.
          */}
          <Table
            striped
            highlightOnHover
            withTableBorder
            stickyHeader={stickyColumnHeaderTopPx != null}
            stickyHeaderOffset={stickyColumnHeaderTopPx ?? 0}
            {...getAdminTableProps(1080)}
          >
            <colgroup>
              {AUDIT_EVENTS_COLUMN_KEYS.map((key) => (
                <col key={key} style={{ width: columnWidths[key] }} />
              ))}
            </colgroup>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="flex-end"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[0]}
                      onPointerDown={(e) => handleBoundaryPointerDown(0, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="space-between"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <Text component="span" fw={600} size="sm" style={{ alignSelf: 'center' }}>
                      Time
                    </Text>
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[1]}
                      onPointerDown={(e) => handleBoundaryPointerDown(1, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="space-between"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <Text component="span" fw={600} size="sm" style={{ alignSelf: 'center' }}>
                      Event Type
                    </Text>
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[2]}
                      onPointerDown={(e) => handleBoundaryPointerDown(2, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="space-between"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <Text component="span" fw={600} size="sm" style={{ alignSelf: 'center' }}>
                      Status
                    </Text>
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[3]}
                      onPointerDown={(e) => handleBoundaryPointerDown(3, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="space-between"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <Text component="span" fw={600} size="sm" style={{ alignSelf: 'center' }}>
                      User
                    </Text>
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[4]}
                      onPointerDown={(e) => handleBoundaryPointerDown(4, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Group
                    wrap="nowrap"
                    justify="space-between"
                    align="stretch"
                    gap={4}
                    style={{ width: '100%' }}
                  >
                    <Text component="span" fw={600} size="sm" style={{ alignSelf: 'center' }}>
                      Summary
                    </Text>
                    <ColumnResizeGrip
                      boundaryLabel={AUDIT_COLUMN_BOUNDARY_LABELS[5]}
                      onPointerDown={(e) => handleBoundaryPointerDown(5, e)}
                    />
                  </Group>
                </Table.Th>
                <Table.Th style={{ ...headerThStyle, verticalAlign: 'middle' }}>
                  <Text component="span" fw={600} size="sm">
                    Actions
                  </Text>
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {groups.map((group) => {
                const groupKey = group.correlationId || group.primaryEvent.id;
                const expanded = expandedGroupKeys.has(groupKey);

                return (
                  <AuditEventGroupRows
                    key={groupKey}
                    group={group}
                    groupKey={groupKey}
                    expanded={expanded}
                    onToggleExpanded={() => toggleGroupExpanded(groupKey)}
                    onViewDetails={handleViewDetails}
                  />
                );
              })}
            </Table.Tbody>
          </Table>
        </Box>
      )}

      {/* Pagination — only rendered when pagination props are provided */}
      {!loading && events.length > 0 && onNextPage && onPrevPage && currentPage != null && (
        <Group justify="space-between">
          <Text size="sm" c={semanticColors.text.secondary}>
            {formatAuditEventsCountLabel(events.length, totalCount, isTruncated)}
          </Text>
          <Group>
            <Button
              variant="light"
              disabled={currentPage === 1}
              onClick={onPrevPage}
            >
              Previous
            </Button>
            <Text size="sm">Page {currentPage}</Text>
            <Button variant="light" disabled={!hasMore} onClick={onNextPage}>
              Next
            </Button>
          </Group>
        </Group>
      )}

      {/* Event Details Modal */}
      <AppModal
        id="audit-event-details"
        opened={detailsOpened}
        onClose={closeDetails}
        title="Audit Event Details"
        size="lg"
        footer={null}
        zIndex={detailsZIndex}
      >
        {selectedEvent && (
          <Stack gap="md">
            <Group justify="space-between">
              <Text fw={500}>Event ID</Text>
              <Code>{selectedEvent.id}</Code>
            </Group>

            <Group justify="space-between">
              <Text fw={500}>Time</Text>
              <Text>
                {formatInstantLocal(selectedEvent.occurredAtUtc)}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text fw={500}>Event Type</Text>
              <Code>{selectedEvent.eventTypeCode}</Code>
            </Group>

            <Group justify="space-between">
              <Text fw={500}>Status</Text>
              <Badge color={getStatusColor(selectedEvent.statusCode)}>
                {selectedEvent.statusCode}
              </Badge>
            </Group>

            <Divider />

            {/* User Info */}
            {(selectedEvent.userId || selectedEvent.userName) && (
              <Group justify="space-between">
                <Text fw={500}>User</Text>
                <Stack gap={0} align="flex-end">
                  {selectedEvent.userName && (
                    <Text>{selectedEvent.userName}</Text>
                  )}
                  {selectedEvent.userId && (
                    <Text size="xs">
                      <Code>{selectedEvent.userId}</Code>
                    </Text>
                  )}
                </Stack>
              </Group>
            )}

            {(selectedEvent.actorUserId || selectedEvent.actorUserName) && (
              <Group justify="space-between">
                <Text fw={500}>Actor</Text>
                <Stack gap={0} align="flex-end">
                  {selectedEvent.actorUserName && (
                    <Text>{selectedEvent.actorUserName}</Text>
                  )}
                  {selectedEvent.actorUserId && (
                    <Text size="xs">
                      <Code>{selectedEvent.actorUserId}</Code>
                    </Text>
                  )}
                </Stack>
              </Group>
            )}

            {/* Entity Info */}
            {(selectedEvent.entityType || selectedEvent.entityId) && (
              <>
                <Divider />
                <Group justify="space-between">
                  <Text fw={500}>Entity</Text>
                  <Text>
                    {selectedEvent.entityType}
                    {selectedEvent.entityId &&
                      ` #${selectedEvent.entityId}`}
                  </Text>
                </Group>
              </>
            )}

            {/* Summary */}
            {selectedEvent.summary && (
              <>
                <Divider />
                <div>
                  <Text fw={500} mb="xs">
                    Summary
                  </Text>
                  <Text>{selectedEvent.summary}</Text>
                </div>
              </>
            )}

            {/* Metadata */}
            {selectedEvent.metadata &&
              Object.keys(selectedEvent.metadata).length > 0 && (
                <>
                  <Divider />
                  <div>
                    <Text fw={500} mb="xs">
                      Metadata
                    </Text>
                    <Code
                      block
                      style={{ maxHeight: 200, overflow: 'auto' }}
                    >
                      {JSON.stringify(selectedEvent.metadata, null, 2)}
                    </Code>
                  </div>
                </>
              )}

            {/* Correlation & Tracing */}
            {(selectedEvent.correlationId || selectedEvent.traceId) && (
              <>
                <Divider />
                {selectedEvent.correlationId && (
                  <Group justify="space-between">
                    <Text fw={500}>Correlation ID</Text>
                    <Code>{selectedEvent.correlationId}</Code>
                  </Group>
                )}
                {selectedEvent.traceId && (
                  <Group justify="space-between">
                    <Text fw={500}>Trace ID (App Insights)</Text>
                    {selectedEvent.appInsightsTraceUrl ? (
                      <Anchor
                        href={selectedEvent.appInsightsTraceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <Group gap={4}>
                          <Code style={{ cursor: 'pointer' }}>
                            {selectedEvent.traceId}
                          </Code>
                          <IconExternalLink size={12} />
                        </Group>
                      </Anchor>
                    ) : (
                      <Code>{selectedEvent.traceId}</Code>
                    )}
                  </Group>
                )}
              </>
            )}

            {/* Visibility */}
            <Divider />
            <Group justify="space-between">
              <Text fw={500}>Visibility</Text>
              <Badge
                color={selectedEvent.isUserVisible ? brandColors.success : brandColors.neutral}
                variant="light"
              >
                {selectedEvent.visibilityScope}
                {selectedEvent.isUserVisible
                  ? ' (visible to user)'
                  : ' (admin only)'}
              </Badge>
            </Group>
          </Stack>
        )}
      </AppModal>
    </>
  );
};

/**
 * Primary row plus optional expanded child rows for a correlation group.
 * Kept as a fragment of `Table.Tr` so columns stay under the shared colgroup.
 */
const AuditEventGroupRows = ({
  group,
  groupKey,
  expanded,
  onToggleExpanded,
  onViewDetails,
}: {
  group: EventGroup;
  groupKey: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  onViewDetails: (event: AuditEvent) => void;
}) => {
  const primary = group.primaryEvent;

  return (
    <>
      <Table.Tr>
        <Table.Td>
          {group.hasMultiple ? (
            <UnstyledButton
              onClick={onToggleExpanded}
              aria-expanded={expanded}
              aria-label={expanded ? 'Collapse related events' : 'Expand related events'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
              }}
            >
              <IconChevronRight
                size={14}
                style={{
                  transform: expanded ? 'rotate(90deg)' : undefined,
                  transition: 'transform 120ms ease',
                }}
              />
            </UnstyledButton>
          ) : (
            <Box w={28} h={28} aria-hidden />
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" truncate>
            {formatInstantLocal(primary.occurredAtUtc)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={4} wrap="nowrap">
            <Text
              size="sm"
              truncate
              style={{ fontFamily: 'monospace', minWidth: 0, flex: 1 }}
            >
              {primary.eventTypeCode}
            </Text>
            {group.hasMultiple && (
              <Badge size="xs" variant="light" style={{ flexShrink: 0 }}>
                +{group.events.length - 1}
              </Badge>
            )}
          </Group>
        </Table.Td>
        <Table.Td>
          <Badge color={getStatusColor(primary.statusCode)} size="sm">
            {primary.statusCode}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Tooltip
            label={primary.userId || primary.actorUserId || 'No user'}
            disabled={!primary.userId && !primary.actorUserId}
          >
            <Text size="sm" truncate>
              {getUserDisplay(primary)}
            </Text>
          </Tooltip>
        </Table.Td>
        <Table.Td>
          <Text size="sm" lineClamp={2}>
            {primary.summary || '-'}
          </Text>
        </Table.Td>
        <Table.Td>
          <Tooltip label="View Details">
            <ActionIcon
              variant="subtle"
              color={actionColors.preview}
              onClick={() => onViewDetails(primary)}
              aria-label={`View details for ${primary.eventTypeCode}`}
            >
              <IconEye size={16} />
            </ActionIcon>
          </Tooltip>
        </Table.Td>
      </Table.Tr>

      {expanded &&
        group.events.slice(1).map((event) => (
          <Table.Tr key={`${groupKey}-${event.id}`}>
            <Table.Td />
            <Table.Td>
              <Text size="xs" c={semanticColors.text.secondary} truncate>
                {formatInstantLocal(event.occurredAtUtc)}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs" truncate style={{ fontFamily: 'monospace' }}>
                {event.eventTypeCode}
              </Text>
            </Table.Td>
            <Table.Td>
              <Badge color={getStatusColor(event.statusCode)} size="xs">
                {event.statusCode}
              </Badge>
            </Table.Td>
            <Table.Td>
              <Text size="xs" truncate>
                {getUserDisplay(event)}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="xs" c={semanticColors.text.secondary} lineClamp={2}>
                {event.summary || '-'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Tooltip label="View Details">
                <ActionIcon
                  variant="subtle"
                  color={actionColors.preview}
                  size="sm"
                  onClick={() => onViewDetails(event)}
                  aria-label={`View details for ${event.eventTypeCode}`}
                >
                  <IconEye size={14} />
                </ActionIcon>
              </Tooltip>
            </Table.Td>
          </Table.Tr>
        ))}
    </>
  );
};
